import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { debounceTime, filter, fromEvent, merge, skip, take } from 'rxjs';

import { CookieService } from '../cookie/cookie.service';
import { sanitizeUrl } from '../helpers';
import { PlatformService } from '../platform/platform.service';
import { SERVER_REQUEST, SERVER_RESPONSE } from '../server/server.tokens';
import { WindowRefService } from '../window-ref/window-ref.service';

export const REDIRECT_COOKIE = 'hdms-redirect';

@Injectable({
  providedIn: 'root'
})
export class RedirectService {
  private readonly win = inject<WindowRefService>(WindowRefService).nativeWindow;
  private readonly request = inject(SERVER_REQUEST, { optional: true });
  private readonly response = inject(SERVER_RESPONSE, { optional: true });
  private readonly cookieService = inject(CookieService);
  private readonly platform = inject(PlatformService);
  private readonly router = inject(Router);

  private blockSubsequent = false;
  private redirectUrl: string;

  constructor() {
    this.cleanupOnNavigation();
  }

  /**
   * Indicate there's a redirect in progress
   */
  get isRedirecting(): boolean {
    return !!this.redirectUrl;
  }

  private get redirectCookie(): string[] {
    return this.cookieService.get<string[]>(REDIRECT_COOKIE);
  }

  /**
   * Redirect on server or client, including redirect-loop prevention.
   * Loop prevention is required in case of automated redirects e.g.
   * by evaluating login status and redirecting to SSO.
   * If a login attempt fails for unknown reason, SSO still would
   * redirect back to current page, leading to a loop.
   * Prevention is mainly done by checking a temporary cookie,
   * which will be bypassed on page reloads to allow re-triggering the redirect.
   * @param url
   * @param preventRedirectLoop
   * @param blockSubsequent
   */
  redirectTo(url: string, preventRedirectLoop = false, blockSubsequent = false): boolean {
    const canRedirect =
      !this.blockSubsequent && this.urlDiffers(url) && (!preventRedirectLoop || this.canRedirect(url, true));

    if (canRedirect) {
      this.cookieService.set(REDIRECT_COOKIE, this.getRedirectCookieVal(url));
      this.redirectUrl = url;

      if (this.platform.isServer) {
        this.response.status(302);
        this.response.setHeader('Location', url);
      } else {
        this.win.location.assign(url);
      }
    }

    this.blockSubsequent ||= blockSubsequent;

    return canRedirect;
  }

  /**
   * Check if a redirect can happen if
   * - current path + optional redirect path aren't stored in cookie
   * - current navigation was no page reload
   */
  canRedirect(url?: string, reset = false): boolean {
    const redirectCookie = this.redirectCookie;
    const cookiePaths = url ? redirectCookie?.join() : redirectCookie?.[0];
    const currentPaths = url ? this.getRedirectCookieVal(url).join() : this.getCurrentPath();
    const canRedirect = cookiePaths !== currentPaths;

    if (reset && !canRedirect) {
      this.resetRedirectState();
    }

    return !this.blockSubsequent && canRedirect;
  }

  resetRedirectState(): void {
    this.cookieService.delete(REDIRECT_COOKIE);
  }

  /**
   * Prevent invoking redirects to same url multiple times
   * @param url
   * @private
   */
  private urlDiffers(url: string): boolean {
    return this.redirectUrl !== url;
  }

  private getCurrentPath(): string {
    return this.request?.originalUrl || this.win.location.pathname;
  }

  private getRedirectCookieVal(url: string): string[] {
    return [this.getCurrentPath(), sanitizeUrl(url)];
  }

  /**
   * Clean up a previously set redirect cookie
   * - on page unload in case no new redirect is in advance
   * - on subsequent router navigation
   * @private
   */
  private cleanupOnNavigation(): void {
    if (this.platform.isBrowser && this.redirectCookie) {
      merge(
        fromEvent(this.win, 'beforeunload'),
        this.router.events.pipe(
          filter(e => e instanceof NavigationStart),
          // debounce repeated route changes caused by route guards or similar
          debounceTime(500),
          skip(1)
        )
      )
        .pipe(
          filter(() => !this.isRedirecting),
          take(1)
        )
        .subscribe(() => this.resetRedirectState());
    }
  }
}
